import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {mergeMap, Observable} from 'rxjs';
import {PaymentProviderConfiguration} from '../models/payments/paymentProviderConfiguration.model';
import {JobPayment} from '../models/payments/jobPayment.model';
import {InitiatePaymentRequest} from '../models/payments/initiate.payment.request';
import {AuthState} from '../state/auth.state';
import {Select} from '@ngxs/store';
import {map} from 'rxjs/operators';
import {CompletePaymentRequest} from '../models/payments/complete.payment.request';
import {RefundePaymentRequest} from '../models/payments/refund.payment.request';
import {CancelPaymentRequest} from '../models/payments/cancel.payment.request';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {
  @Select(AuthState.isAuthenticated)
  private isAuthenticated$: Observable<boolean>;
  private PAYMENTS_URL: string = environment.BASE_URL + 'payment';
  private PUBLIC_PAYMENTS_URL: string = environment.BASE_URL + 'public/payment';
  private JOB_PAYMENT_URL: string = environment.BASE_URL + 'job';

  constructor(private http: HttpClient) {
  }

  public getProviders(): Observable<Array<PaymentProviderConfiguration>> {
    return this.http.get<Array<PaymentProviderConfiguration>>(`${this.PAYMENTS_URL}/provider`);
  }

  public getProvider(id: string): Observable<PaymentProviderConfiguration> {
    return this.http.get<PaymentProviderConfiguration>(`${this.PAYMENTS_URL}/provider/${id}`);
  }

  public createProvider(provider: PaymentProviderConfiguration): Observable<PaymentProviderConfiguration> {
    return this.http.post<PaymentProviderConfiguration>(`${this.PAYMENTS_URL}/provider`, provider);
  }

  public updateProvider(provider: PaymentProviderConfiguration): Observable<PaymentProviderConfiguration> {
    return this.http.patch<PaymentProviderConfiguration>(`${this.PAYMENTS_URL}/provider/${provider.id}`, provider);
  }

  public deleteProvider(provider: PaymentProviderConfiguration): Observable<PaymentProviderConfiguration> {
    return this.http.delete<PaymentProviderConfiguration>(`${this.PAYMENTS_URL}/provider/${provider.id}`);
  }

  public getPaymentsProviders(): Observable<string[]> {
    return this.http.get<string[]>(`${this.PAYMENTS_URL}/providers`);
  }

  public getJobPayment(id: string): Observable<JobPayment> {
    return this.http.get<JobPayment>(`${this.JOB_PAYMENT_URL}/${id}/payment`);
  }

  public updateJobPayment(id: string, jobPayment: JobPayment): Observable<JobPayment> {
    return this.http.patch<JobPayment>(`${this.JOB_PAYMENT_URL}/${id}/payment`, jobPayment);
  }

  public initiatePayment(request: InitiatePaymentRequest, headers?: { [header: string]: string | string[] }): Observable<any> {
    return this.isAuthenticated$.pipe(
      map(authenticated => {
        if (authenticated) {
          return this.http.post<any>(`${this.PAYMENTS_URL}/initiate/payment`, request);
        }
        return this.http.post<any>(`${this.PUBLIC_PAYMENTS_URL}/initiate/payment`, request, {headers});
      }),
      mergeMap(res => res)
    );
  }

  public completePayment(request: CompletePaymentRequest, headers?: { [header: string]: string | string[] }): Observable<any> {
    return this.isAuthenticated$.pipe(
      map(authenticated => {
        if (authenticated) {
          return this.http.post<any>(`${this.PAYMENTS_URL}/complete/payment`, request);
        }
        return this.http.post<any>(`${this.PUBLIC_PAYMENTS_URL}/complete/payment`, request, {headers});
      }),
      mergeMap(res => res)
    );
  }

  public cancelPayment(request: CancelPaymentRequest, headers?: { [header: string]: string | string[] }): Observable<any> {
    return this.isAuthenticated$.pipe(
      map(authenticated => {
        if (authenticated) {
          return this.http.post<any>(`${this.PAYMENTS_URL}/cancel/payment`, request);
        }
        return this.http.post<any>(`${this.PUBLIC_PAYMENTS_URL}/cancel/payment`, request, {headers});
      }),
      mergeMap(res => res)
    );
  }

  public refundPayment(request: RefundePaymentRequest, headers?: { [header: string]: string | string[] }): Observable<any> {
    return this.isAuthenticated$.pipe(
      map(authenticated => {
        if (authenticated) {
          return this.http.post<any>(`${this.PAYMENTS_URL}/refund/payment`, request);
        }
        return this.http.post<any>(`${this.PUBLIC_PAYMENTS_URL}/refund/payment`, request, {headers});
      }),
      mergeMap(res => res)
    );
  }
}
